import {Alert, Badge, Card, Col, Icon, Row, Spin} from 'antd'
import FileSaver from 'file-saver'
import React, {FC, ReactNode, useState} from 'react'
import {withTranslation, WithTranslation} from 'react-i18next'
import HttpService from '../../core/services/http.service'
import {container} from '../../inversify.config'
import {NotificationItem} from 'src/stores/systemNotifications/notification-store'
import ExportNotification from './export-notification'
import BulkInsertNotification from './bulk-insert-notification'
import moment from 'moment'


export enum NotificationType {
    //Insert
    BulkInsertTaskTemplate = "BulkInsertTaskTemplate",
    BulkInsertEmployeesTemplate = 'BulkInsertEmployeesTemplate',
    BulkMembersAssigmentTemplate = 'BulkMembersAssignmentTemplate',
    BulkProjectTaskAssignmentTemplate = 'BulkProjectTaskAssignmentTemplate',
    BulkInsertProjectsTemplate = "BulkInsertProjectsTemplate",
    BulkInsertTimesheetTemplate = 'BulkInsertTimesheetTemplate',    

    //Export
    TimeSheetExportQueryHandler = 'TimeSheetExportQueryHandler',
    TimeSheetExportStandardQueryHandler = 'TimeSheetExportStandardQueryHandler',
    TimeSheetExportWithParametersQueryHandler = 'TimeSheetExportWithParametersQueryHandler',
    ExportCompanyQueryHandler = 'ExportCompanyQueryHandler',
    ExportLogsReportQueryHandler = 'ExportLogsReportQueryHandler',
    ExportBulkTemplateQueryHandler = 'ExportBulkTemplateQueryHandler',    
    ExportBulkEmployeeTemplateQueryHandler = 'ExportBulkEmployeeTemplateQueryHandler',    
    ExportMembersAssigmentTemplate = 'ExportMembersAssignmentTemplateQueryHandler',    
    ExportProjectTaskTemplateQueryHandler = "ExportProjectTaskTemplateQueryHandler",    
    ExportEmployeesQueryHandler = 'ExportEmployeesQueryHandler',
    ExportProjectsTemplateQueryHandler = "ExportProjectTemplateQueryHandler",    
    ExportBulkTaskTemplateQueryHandler = "ExportBulkTaskTemplateQueryHandler",
    ExportProjectsQueryHandler = "ExportProjectsQueryHandler"
}

interface Props extends WithTranslation {
    notification: NotificationItem
    openNotification?: () => void
    removeNotification?: () => void
}

const NotificationView: FC<Props> = (props) => {

    const { t, notification } = props
    const [isBusy, setIsBusy] = useState(false)
    const httpService = container.get(HttpService)    

    const downloadExcel = async (data: any, fileName: string) => {
        setIsBusy(true)
        const result = await httpService.get(`api/v1/admin/filestorage/downloadExcel?url=` + data, {
            responseType: 'arraybuffer',
        })
        const blob = new Blob([result.data as any], { type: result.headers['content-type'] });
        (FileSaver as any).saveAs(blob, `${t(fileName)}.xlsx`);
        setIsBusy(false);
    }

    const getNotificationContent = (): ReactNode => {
        if (notification.data.MessageError) {
            return <Alert message={notification.data.MessageError} banner type="error" showIcon />
        }
        else if (notification.data.MessageNotfound) {
            return <Alert message={t(notification.data.MessageNotfound)} banner type="info" showIcon />
        }   
        
        const currentDate = moment(notification.creationTime).local().format('DD-MM-YYYY');
        const currentTime = moment(notification.creationTime).local().format('hh:mm a');

        switch (notification.type) {
            //Insert
            case NotificationType.BulkInsertTimesheetTemplate:
                return <BulkInsertNotification notification={notification} />

            case NotificationType.BulkInsertEmployeesTemplate:
                return <BulkInsertNotification notification={notification} />

            case NotificationType.BulkMembersAssigmentTemplate:
                return <BulkInsertNotification notification={notification} />

            case NotificationType.BulkProjectTaskAssignmentTemplate:
                return <BulkInsertNotification notification={notification} />

            case NotificationType.BulkInsertProjectsTemplate:
                return <BulkInsertNotification notification={notification} />

            case NotificationType.BulkInsertTaskTemplate:
                return <BulkInsertNotification notification={notification} />


            //Export
            case NotificationType.TimeSheetExportQueryHandler:
                return <ExportNotification
                    title={`${t(notification.name)} ${currentDate}`}
                    fileName={`${t(notification.name)}-${currentDate}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                />
            case NotificationType.TimeSheetExportStandardQueryHandler:
                return <ExportNotification
                    title={`${t(notification.name)} ${currentDate}`}
                    fileName={`${t(notification.name)}-${currentDate}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                />
            case NotificationType.TimeSheetExportWithParametersQueryHandler:
                const newName = notification.name.split("_");
                return <ExportNotification
                    title={`${t(newName[0])} ${newName[1]}`}
                    fileName={`${t(newName[0])} ${newName[1]}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                />
            case NotificationType.ExportCompanyQueryHandler:
                return <ExportNotification
                    title={`${t(notification.name)} ${currentDate}`}
                    fileName={`${t(notification.name)}-${currentDate}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                />
            case NotificationType.ExportLogsReportQueryHandler:
                return <ExportNotification
                    title={`${t(notification.name)} ${currentDate}`}
                    fileName={`${t(notification.name)}-${currentDate}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                />
            case NotificationType.ExportBulkTemplateQueryHandler:
                return <ExportNotification
                    title={`${t(notification.name)} ${currentDate}`}
                    fileName={`${t(notification.name)}-${currentDate}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                />

            case NotificationType.ExportBulkEmployeeTemplateQueryHandler:
                return <ExportNotification
                    title={`${t(notification.name)} ${currentDate}`}
                    fileName={`${t(notification.name)}-${currentDate}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                />        

            case NotificationType.ExportMembersAssigmentTemplate:
                return <ExportNotification
                    title={`${t(notification.name)} ${currentDate}`}
                    fileName={`${t(notification.name)}-${currentDate}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                /> 

            case NotificationType.ExportProjectTaskTemplateQueryHandler:
                return <ExportNotification
                    title={`${t(notification.name)} ${currentDate}`}
                    fileName={`${t(notification.name)}-${currentDate}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                />

            case NotificationType.ExportEmployeesQueryHandler:
                return <ExportNotification
                    title={`${t(notification.name)} ${currentDate}`}
                    fileName={`${t(notification.name)}-${currentDate}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                />            

            case NotificationType.ExportProjectsTemplateQueryHandler:
                return <ExportNotification
                    title={`${t(notification.name)} ${currentDate}`}
                    fileName={`${t(notification.name)}-${currentDate}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                />

            case NotificationType.ExportBulkTaskTemplateQueryHandler:
                return <ExportNotification
                    title={`${t(notification.name)} ${currentDate}`}
                    fileName={`${t(notification.name)}-${currentDate}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                />

            case NotificationType.ExportProjectsQueryHandler:
                return <ExportNotification
                    title={`${t(notification.name)} ${currentDate}`}
                    fileName={`${t(notification.name)}-${currentDate}`}
                    notification={notification}
                    downloadExcel={downloadExcel}
                    currentTime={`${currentTime}`}
                />
        }

        return <>{notification.type}</>
    }

    return (
        <div className="notification-item">
            <Spin spinning={isBusy}>
                <Card
                    title={
                        <Row style={{ flexFlow: 'row', marginTop: '-12px' }}>
                            <Col>
                                <span style={{ marginRight: '10px' }}>
                                    <Badge count={notification.readed ? 0 : undefined} dot>
                                        <Icon type="Notification" />
                                    </Badge>
                                </span>
                            </Col>
                            <Col >
                                <div style={{ whiteSpace: 'normal', textOverflow: 'ellipsis', overflow: 'auto' }}>{t(notification.description)}</div>
                            </Col>
                        </Row>
                    }
                    extra={
                        <Icon type="close-square"
                            onClick={() => {
                                if (props.removeNotification) props.removeNotification()
                            }}
                            style={{ fontWeight: 'bold', cursor: 'pointer', marginRight: '8px' }}
                        />
                    }
                >
                    {getNotificationContent()}
                </Card>
            </Spin>
        </div>
    )
}

export default withTranslation()(NotificationView)
